import React from 'react';
import { Briefcase, Mail, Phone, MapPin, Clock, Calendar, ExternalLink, MessageCircle } from 'lucide-react';
import { CONTACT_INFO, NAV_ITEMS } from '@utils/constants';

const Footer = ({ scrollToSection, openLegal }) => {
  return (
    <footer className="bg-slate-900 text-slate-300 py-16 border-t border-slate-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-4 gap-10 mb-12">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-10 h-10 bg-gradient-to-br from-indigo-600 to-violet-600 rounded-xl flex items-center justify-center shadow-lg shadow-indigo-600/20">
                <Briefcase className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold text-white">Placement Point</span>
            </div>
            <p className="text-sm text-slate-400 mb-4 leading-relaxed">
              Your partner in landing dream jobs locally and globally. We handle the job search grind so you can focus on what matters.
            </p>
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <Calendar className="w-3.5 h-3.5" />
              <span>{CONTACT_INFO.workingHours}</span>
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-semibold text-white mb-4 text-sm uppercase tracking-wider">Services</h4>
            <ul className="space-y-3 text-sm">
              <li className="hover:text-white transition-colors cursor-pointer" onClick={() => scrollToSection('services')}>CV Edit & Optimization</li>
              <li className="hover:text-white transition-colors cursor-pointer" onClick={() => scrollToSection('services')}>Strategic Job Applications</li>
              <li className="hover:text-white transition-colors cursor-pointer" onClick={() => scrollToSection('services')}>Tailored Cover Letters</li>
              <li className="hover:text-white transition-colors cursor-pointer" onClick={() => scrollToSection('services')}>LinkedIn Optimization</li>
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="font-semibold text-white mb-4 text-sm uppercase tracking-wider">Company</h4>
            <ul className="space-y-3 text-sm">
              <li 
                className="cursor-pointer hover:text-white transition-colors"
                onClick={() => scrollToSection('process')}
              >
                How It Works
              </li>
              <li 
                className="cursor-pointer hover:text-white transition-colors"
                onClick={() => scrollToSection('success-stories')}
              >
                Success Stories
              </li>
              <li 
                className="cursor-pointer hover:text-white transition-colors"
                onClick={() => scrollToSection('pricing')}
              >
                Pricing
              </li>
              <li 
                className="cursor-pointer hover:text-white transition-colors"
                onClick={() => scrollToSection('jobs')}
              >
                Job Board
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-semibold text-white mb-4 text-sm uppercase tracking-wider">Contact Us</h4>
            <ul className="space-y-3 text-sm">
              <li className="flex items-center gap-2 hover:text-white transition-colors">
                <Mail className="w-4 h-4 text-indigo-400" />
                <a 
                  href={`https://mail.google.com/mail/?view=cm&fs=1&to=${CONTACT_INFO.email}`} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="hover:underline hover:text-indigo-300 transition-colors"
                >
                  {CONTACT_INFO.email}
                </a>
              </li>
              <li className="flex items-center gap-2 hover:text-white transition-colors">
                <Phone className="w-4 h-4 text-indigo-400" />
                <a href={`tel:${CONTACT_INFO.phone.replace(/\s+/g, '')}`}>{CONTACT_INFO.phone}</a>
              </li>
              <li className="flex items-center gap-2 hover:text-white transition-colors">
                <MessageCircle className="w-4 h-4 text-green-400" />
                <a 
                  href={`https://wa.me/${CONTACT_INFO.whatsappRaw}`} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="hover:underline hover:text-green-300 transition-colors"
                >
                  {CONTACT_INFO.whatsapp} (WhatsApp)
                </a>
              </li>
              <li className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-indigo-400" />
                <span>{CONTACT_INFO.address}</span>
              </li>
              <li className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-indigo-400" />
                <span>{CONTACT_INFO.consultationDuration} sessions</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Disclaimer */}
        <div className="bg-slate-800/50 rounded-xl p-4 mb-8 border border-slate-700/50">
          <p className="text-xs text-slate-500 text-center leading-relaxed">
            <span className="text-amber-400 font-semibold">Disclaimer:</span>{' '}
            Placement Point does not guarantee job placement. We provide professional career tools and strategy; 
            the final hiring decision rests solely with the employer or hiring company. Results vary based on market 
            conditions, individual qualifications, and other factors beyond our control.
          </p>
        </div>

        <div className="border-t border-slate-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-slate-500">
          <p>© {new Date().getFullYear()} Placement Point. All rights reserved.</p>
          <div className="flex gap-6">
            <button 
              className="hover:text-white transition-colors"
              onClick={() => openLegal && openLegal('privacy')}
            >
              Privacy Policy
            </button>
            <button 
              className="hover:text-white transition-colors"
              onClick={() => openLegal && openLegal('terms')}
            >
              Terms of Service
            </button>
            <button 
              className="hover:text-white transition-colors"
              onClick={() => openLegal && openLegal('refund')}
            >
              Refund Policy
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
